import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, Stack } from '@mui/material';

import dayjs from 'dayjs';

import { ShipmentResponse } from '@/api/data-contracts';
import { ReactComponent as PrinterIcon } from '@/assets/icons/printer-light-icon.svg';
import { ReactComponent as TruckIcon } from '@/assets/icons/truck-outline-icon.svg';
import AppButton from '@/shared/ui/AppButton';

import { useShipmentLabel } from '../../api/useShipmentLabel';
import { usePickupPointsQuery } from '../../api/useShippingQueries';
import AwbDialogHeader from './AwbDialogHeader';
import ShipmentSummary from './awb/ShipmentSummary';
import { generateAwbResultPaperSx } from './styles';

interface ShipmentDetailsDialogProps {
  shipment: ShipmentResponse;
  onClose: () => void;
}

const ShipmentDetailsDialog: FC<ShipmentDetailsDialogProps> = ({ shipment, onClose }) => {
  const { t } = useTranslation();
  const { openLabel, openingShipmentId } = useShipmentLabel();
  const { data: pickupPoints = [] } = usePickupPointsQuery();

  const senderName = useMemo(() => {
    const point = pickupPoints.find((item) => item.fanClientId === shipment.clientId);
    return point?.name ?? shipment.clientId;
  }, [pickupPoints, shipment.clientId]);

  const generatedAt = shipment.generatedAt ?? shipment.createdAt;
  const subtitle = [
    shipment.orderNumber && t('awb.order_label', { order: shipment.orderNumber }),
    generatedAt && dayjs(generatedAt).format('D MMM YYYY, HH:mm'),
  ]
    .filter(Boolean)
    .join(' · ');

  const total =
    shipment.quotedTotal ??
    (shipment.tariff !== undefined ? shipment.tariff + (shipment.vat ?? 0) : undefined);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        'data-testid': 'shipment-details-dialog',
        sx: { ...generateAwbResultPaperSx, maxWidth: '53.75rem' },
      }}
    >
      <AwbDialogHeader
        icon={<TruckIcon />}
        title={t('awb.details_title')}
        subtitle={subtitle}
        onClose={onClose}
        closeTestId="shipment-details-close"
      />

      <ShipmentSummary
        awbNumber={shipment.awbNumber}
        verifying={shipment.status === 'PENDING'}
        trackingUrl={shipment.trackingUrl}
        pickupDate={shipment.pickupDate ?? shipment.intendedPickupDate}
        senderName={senderName}
        parcels={shipment.parcels}
        weight={shipment.weight}
        recipientName={shipment.recipient?.name}
        recipientLocality={shipment.recipient?.locality}
        total={total}
      />

      <Stack direction="row" justifyContent="flex-end" gap="0.625rem">
        <AppButton variant="secondary" onClick={onClose} data-testid="shipment-details-close-btn">
          {t('Close')}
        </AppButton>
        {!!shipment.awbNumber && (
          <AppButton
            variant="primary"
            startIcon={<PrinterIcon />}
            loading={openingShipmentId === shipment.id}
            onClick={() => openLabel(shipment.id)}
            data-testid="shipment-details-print"
          >
            {t('awb.print_label')}
          </AppButton>
        )}
      </Stack>
    </Dialog>
  );
};

export default ShipmentDetailsDialog;
